import {
  TrashIcon,
  DocumentDuplicateIcon,
  LockOpenIcon,
  LockClosedIcon,
} from "@heroicons/react/20/solid";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import { Template } from "../../type";
import Snapshot from "@components/shared/Snapshot";
import { useTranslation } from "react-i18next";

type Props = {
  template: Template;
  onOpen: (templateId: string) => void;
  onRemove: (templateId: string) => void;
  onDuplicate: (templateId: string) => void;
};

function TemplateItem({ template, onOpen, onRemove, onDuplicate }: Props) {
  const { t } = useTranslation();

  const date = new Date(template.updated_at);
  const ago = formatDistanceToNow(date, {
    addSuffix: true,
  });

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-indigo-400 hover:shadow-lg">
      {/* Overlay actions */}
      <div className="absolute right-3 top-3 z-10 flex flex-col space-y-2 opacity-0 transition group-hover:opacity-100">
        <button
          title={t("dashboard_template_duplicate")}
          className="rounded-full bg-white p-2 shadow hover:bg-indigo-50"
          onClick={(event) => {
            event.stopPropagation();
            onDuplicate(template.id);
          }}
        >
          <DocumentDuplicateIcon className="h-5 w-5 text-gray-500" />
        </button>
        <button
          title={t("dashboard_template_delete")}
          className="rounded-full bg-white p-2 shadow hover:bg-red-50"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(template.id);
          }}
        >
          <TrashIcon className="h-5 w-5 text-red-400" />
        </button>
      </div>
      {/* Preview */}
      <div
        className="relative mb-2 flex h-56 cursor-pointer flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-5 transition group-hover:scale-[1.03] group-hover:bg-indigo-50"
        onClick={() => {
          onOpen(template.id);
        }}
      >
        <Snapshot template={template} />
        {/* Status badge */}
        <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-white/80 px-2 py-1 text-xs font-semibold text-gray-700 shadow">
          {template.public ? (
            <LockOpenIcon className="mr-1 h-4 w-4 text-green-500" />
          ) : (
            <LockClosedIcon className="mr-1 h-4 w-4 text-gray-400" />
          )}
          {template.public
            ? t("dashboard_template_public")
            : t("dashboard_template_private")}
        </span>
      </div>
      {/* Info */}
      <div className="flex flex-col space-y-1 px-4 pb-4">
        <div className="flex items-center justify-between">
          <h2
            className="truncate text-base font-semibold text-gray-900"
            title={template.title}
          >
            {template.title}
          </h2>
          <span className="ml-2 text-xs text-gray-400">{ago}</span>
        </div>
      </div>
    </div>
  );
}

export default TemplateItem;
